import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/Styles1.css';
import './CRUD.css';
import './PeriodosCRUD.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNotification } from '../components/NotificationSystem';
import SEO from '../components/SEO';
import { fetchV2 } from '../utils/apiV2';
import Icon from '../components/Icon';

const ADVERTENCIA_ACTIVACION =
  'Una vez iniciada la evaluación, esta configuración quedará asociada al periodo y los cambios ' +
  'posteriores al banco de competencias no modificarán esta evaluación.';

const MODELOS_PONDERACION = [
  { codigo: 'CON_COLABORADORES', etiqueta: 'Con colaboradores (Jefe 45% / Colab. 25% / Par 20% / Auto 10%)' },
  { codigo: 'SIN_COLABORADORES', etiqueta: 'Sin colaboradores (Jefe 60% / Par 30% / Auto 10%)' },
  { codigo: 'GERENTE_GENERAL', etiqueta: 'Gerente General (Jefe 60% / Colab. 30% / Auto 10%, sin par)' },
  { codigo: 'OP_CAMPO_180', etiqueta: 'Operación/Campo 180° (Auto 20% / Jefe 50%)' },
];

function formularioVacio() {
  return {
    codigo: '',
    nombre: '',
    fecha_inicio: '',
    fecha_fin: '',
    aplica_meridian_control: true,
    peso_competencias: 50,
    peso_meridian: 30,
    peso_hseq: 20,
    observaciones: '',
  };
}

function estadoBadgeClase(estado) {
  if (estado === 'ACTIVO') return 'estado-badge-activo';
  if (estado === 'CERRADO') return 'estado-badge-cerrado';
  return 'estado-badge-borrador';
}

function sumaPesos(p) {
  const competencias = Number(p.peso_competencias) || 0;
  const meridian = p.aplica_meridian_control ? Number(p.peso_meridian) || 0 : 0;
  const hseq = Number(p.peso_hseq) || 0;
  return competencias + meridian + hseq;
}

function PeriodosCRUD({ onLogout }) {
  const { success, error: showError } = useNotification();
  const navigate = useNavigate();

  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(null); // vista resumen / histórico
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formulario, setFormulario] = useState(formularioVacio());
  const [guardando, setGuardando] = useState(false);
  const [showConfirmActivar, setShowConfirmActivar] = useState(false);

  const [niveles, setNiveles] = useState([]);
  const [showGenerarForm, setShowGenerarForm] = useState(false);
  const [formGenerar, setFormGenerar] = useState({ codigo_banco: '', codigo_modelo: '' });
  const [generando, setGenerando] = useState(false);
  const [resultadoGeneracion, setResultadoGeneracion] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${apiUrl}/api/cargos/niveles`)
      .then((r) => r.json())
      .then((result) => {
        if (result.success) setNiveles(result.data);
      })
      .catch(() => {});
  }, []);

  const fetchPeriodos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { ok, status, data } = await fetchV2('api/v2/periodos');
      if (status === 401) {
        throw new Error('Sesión sin token válido para el módulo de configuración (api/v2). Vuelva a iniciar sesión.');
      }
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al cargar los períodos');
      }
      setPeriodos(data.data || []);
    } catch (e) {
      setError(e.message || 'Error al cargar los períodos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPeriodos();
  }, [fetchPeriodos]);

  const handleBackClick = () => navigate('/admin');

  const handleAddClick = () => {
    setIsEditing(false);
    setFormulario(formularioVacio());
    setShowForm(true);
  };

  const handleEditClick = (periodo) => {
    setIsEditing(true);
    setFormulario({
      id_periodo: periodo.id_periodo,
      codigo: periodo.codigo,
      nombre: periodo.nombre,
      fecha_inicio: periodo.fecha_inicio || '',
      fecha_fin: periodo.fecha_fin || '',
      aplica_meridian_control: !!Number(periodo.aplica_meridian_control),
      peso_competencias: periodo.peso_competencias,
      peso_meridian: periodo.peso_meridian,
      peso_hseq: periodo.peso_hseq,
      observaciones: periodo.observaciones || '',
    });
    setShowForm(true);
  };

  const handleCancelForm = () => setShowForm(false);

  const handleCampoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormulario((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = {
        nombre: formulario.nombre.trim(),
        fecha_inicio: formulario.fecha_inicio || null,
        fecha_fin: formulario.fecha_fin || null,
        aplica_meridian_control: formulario.aplica_meridian_control,
        peso_competencias: Number(formulario.peso_competencias),
        peso_meridian: Number(formulario.peso_meridian),
        peso_hseq: Number(formulario.peso_hseq),
        observaciones: formulario.observaciones.trim() || null,
      };

      let resp;
      if (isEditing) {
        resp = await fetchV2(`api/v2/periodos/${formulario.id_periodo}`, { method: 'PUT', body: JSON.stringify(cuerpo) });
      } else {
        resp = await fetchV2('api/v2/periodos', { method: 'POST', body: JSON.stringify({ ...cuerpo, codigo: formulario.codigo.trim() }) });
      }

      if (!resp.ok || !resp.data?.success) {
        throw new Error(resp.data?.message || `Error al ${isEditing ? 'actualizar' : 'crear'} el período`);
      }

      setShowForm(false);
      success(isEditing ? 'Período actualizado' : 'Período creado', isEditing ? 'Los cambios se guardaron con éxito' : 'El período quedó en estado BORRADOR');
      fetchPeriodos();
    } catch (e) {
      showError('Error de operación', e.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleVerResumen = (periodo) => {
    setPeriodoSeleccionado(periodo);
    setShowGenerarForm(false);
    setResultadoGeneracion(null);
    setFormGenerar({ codigo_banco: niveles[0]?.codigo || '', codigo_modelo: '' });
  };

  const handleCerrarResumen = () => {
    setPeriodoSeleccionado(null);
    setShowGenerarForm(false);
    setResultadoGeneracion(null);
  };

  const handleGenerarCampoChange = (e) => {
    const { name, value } = e.target;
    setFormGenerar((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerarEvaluaciones = async (e) => {
    e.preventDefault();
    if (!periodoSeleccionado) return;
    setGenerando(true);
    setResultadoGeneracion(null);
    try {
      const { ok, data } = await fetchV2(`api/v2/periodos/${periodoSeleccionado.id_periodo}/generar-evaluaciones`, {
        method: 'POST',
        body: JSON.stringify(formGenerar),
      });
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al generar las evaluaciones');
      }
      setResultadoGeneracion(data.data);
      success(
        'Generación completada',
        `${data.data.evaluaciones_creadas ?? 0} evaluaciones y ${data.data.asignaciones_creadas} asignaciones nuevas`
      );
    } catch (e) {
      showError('No se pudo generar', e.message);
    } finally {
      setGenerando(false);
    }
  };

  const handlePedirActivacion = () => setShowConfirmActivar(true);

  const handleConfirmarActivacion = async () => {
    if (!periodoSeleccionado) return;
    try {
      const { ok, data } = await fetchV2(`api/v2/periodos/${periodoSeleccionado.id_periodo}/activar`, { method: 'POST' });
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al activar el período');
      }
      success('Período activado', data.message || 'La configuración quedó congelada');
      setShowConfirmActivar(false);
      setPeriodoSeleccionado(null);
      fetchPeriodos();
    } catch (e) {
      showError('No se pudo activar', e.message);
      setShowConfirmActivar(false);
    }
  };

  const handleCerrarPeriodo = async (periodo) => {
    if (!window.confirm(`¿Cerrar el período "${periodo.nombre}"? Quedará de solo lectura y no podrá reabrirse.`)) {
      return;
    }
    try {
      const { ok, data } = await fetchV2(`api/v2/periodos/${periodo.id_periodo}/cerrar`, { method: 'POST' });
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al cerrar el período');
      }
      success('Período cerrado', 'Quedó de solo lectura');
      setPeriodoSeleccionado(null);
      fetchPeriodos();
    } catch (e) {
      showError('No se pudo cerrar', e.message);
    }
  };

  const renderResumen = (p) => {
    const suma = sumaPesos(p);
    const sumaOk = Math.abs(suma - 100) < 0.01;
    return (
      <div className="periodo-resumen">
        <dl>
          <dt>Nombre</dt>
          <dd>{p.nombre}</dd>
          <dt>Código / Período</dt>
          <dd>{p.codigo}</dd>
          <dt>Estado</dt>
          <dd><span className={`estado-badge ${estadoBadgeClase(p.estado)}`}>{p.estado}</span></dd>
          <dt>Fecha de inicio</dt>
          <dd>{p.fecha_inicio || 'Sin definir'}</dd>
          <dt>Fecha de cierre</dt>
          <dd>{p.fecha_fin || 'Sin definir'}</dd>
          <dt>Componentes (fórmula de composición integral)</dt>
          <dd>
            <ul className="resumen-componentes">
              <li>Competencias: {p.peso_competencias}%</li>
              <li className={!p.aplica_meridian_control ? 'componente-no-aplica' : ''}>
                Meridian Control: {p.aplica_meridian_control ? `${p.peso_meridian}%` : 'No aplica a esta población'}
              </li>
              <li>HSEQ: {p.peso_hseq}%</li>
            </ul>
            <span className={sumaOk ? 'suma-pesos-ok' : 'suma-pesos-error'}>
              Suma de componentes aplicables: {suma.toFixed(2)}%{' '}
              {sumaOk ? <Icon name="check" size={14} title="Correcto" /> : '(debe ser 100% para poder activar)'}
            </span>
          </dd>
          <dt>Descripción / Observaciones</dt>
          <dd>{p.observaciones || 'Sin descripción'}</dd>
        </dl>
      </div>
    );
  };

  return (
    <>
      <SEO
        title="Próximas Evaluaciones - Panel de Administración"
        description="Configura, revisa y activa los períodos de evaluación de desempeño en Meridian Consulting LTDA."
        keywords="períodos de evaluación, configuración de evaluaciones, próximas evaluaciones, Meridian Consulting"
        url="https://evaluacion.meridianltda.com/admin/periodos"
      />
      <div className="crud-container periodos-crud">
        <Header onLogout={onLogout} />

        <main className="crud-main">
          <div className="crud-header">
            <button className="back-button" onClick={handleBackClick}>Volver al Panel</button>
            <div className="crud-header-titles">
              <h1>Próximas Evaluaciones</h1>
              <p className="crud-header-subtitle">Configura, revisa y activa los períodos de evaluación</p>
            </div>
            <button className="add-button" onClick={handleAddClick}>Nuevo Período</button>
          </div>

          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button onClick={fetchPeriodos}>Reintentar</button>
            </div>
          )}

          {showForm && (
            <div className="crud-form-container">
              <form className="crud-form periodo-form" onSubmit={handleSubmit}>
                <h2>{isEditing ? 'Editar Período' : 'Nuevo Período'}</h2>
                <div className="form-body">
                  {!isEditing && (
                    <div className="form-group">
                      <label htmlFor="codigo">Código interno:</label>
                      <input type="text" id="codigo" name="codigo" value={formulario.codigo} onChange={handleCampoChange} required placeholder="2027-ANUAL" />
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="nombre">Nombre:</label>
                    <input type="text" id="nombre" name="nombre" value={formulario.nombre} onChange={handleCampoChange} required placeholder="Evaluación de desempeño 2027" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fecha_inicio">Fecha de inicio:</label>
                      <input type="date" id="fecha_inicio" name="fecha_inicio" value={formulario.fecha_inicio} onChange={handleCampoChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="fecha_fin">Fecha de cierre:</label>
                      <input type="date" id="fecha_fin" name="fecha_fin" value={formulario.fecha_fin} onChange={handleCampoChange} />
                    </div>
                  </div>

                  <div className="form-group form-group-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="aplica_meridian_control"
                        checked={formulario.aplica_meridian_control}
                        onChange={handleCampoChange}
                      />
                      Aplica Meridian Control a esta población
                    </label>
                  </div>

                  <div className="form-row form-row-pesos">
                    <div className="form-group">
                      <label htmlFor="peso_competencias">Peso Competencias (%):</label>
                      <input type="number" id="peso_competencias" name="peso_competencias" value={formulario.peso_competencias} onChange={handleCampoChange} min="0" max="100" step="0.01" required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="peso_meridian">Peso Meridian (%):</label>
                      <input type="number" id="peso_meridian" name="peso_meridian" value={formulario.peso_meridian} onChange={handleCampoChange} min="0" max="100" step="0.01" disabled={!formulario.aplica_meridian_control} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="peso_hseq">Peso HSEQ (%):</label>
                      <input type="number" id="peso_hseq" name="peso_hseq" value={formulario.peso_hseq} onChange={handleCampoChange} min="0" max="100" step="0.01" required />
                    </div>
                  </div>
                  <p className={Math.abs(sumaPesos(formulario) - 100) < 0.01 ? 'suma-pesos-ok' : 'suma-pesos-error'}>
                    Suma: {sumaPesos(formulario).toFixed(2)}% (debe ser exactamente 100% para poder activar este período)
                  </p>

                  <div className="form-group">
                    <label htmlFor="observaciones">Descripción / Observaciones:</label>
                    <textarea id="observaciones" name="observaciones" value={formulario.observaciones} onChange={handleCampoChange} rows="3" />
                  </div>
                </div>

                <div className="form-buttons">
                  <button type="button" className="cancel-button" onClick={handleCancelForm}>Cancelar</button>
                  <button type="submit" className="save-button" disabled={guardando}>
                    {guardando ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Guardar como Borrador'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {periodoSeleccionado && (
            <div className="crud-form-container">
              <div className="crud-form periodo-resumen-modal">
                <h2>Vista resumen — {periodoSeleccionado.codigo}</h2>
                <div className="form-body">
                  {renderResumen(periodoSeleccionado)}

                  {periodoSeleccionado.estado === 'CERRADO' && (
                    <p className="nivel-aviso">Este período está cerrado: la configuración es de solo lectura.</p>
                  )}

                  {showConfirmActivar && (
                    <div className="advertencia-activacion">
                      <strong className="ui-icon-inline"><Icon name="warning" size={14} /> Antes de activar:</strong>
                      <p>{ADVERTENCIA_ACTIVACION}</p>
                    </div>
                  )}

                  {periodoSeleccionado.estado === 'ACTIVO' && showGenerarForm && (
                    <form className="generar-evaluaciones-form" onSubmit={handleGenerarEvaluaciones}>
                      <h3>Generar evaluaciones de los trabajadores</h3>
                      <p className="generar-evaluaciones-ayuda">
                        Crea automáticamente las asignaciones "auto" y, cuando la estructura organizacional lo permita,
                        "jefe" y "colaborador" de todos los empleados activos cuyo nivel resuelto (cargo→nivel o
                        configuración individual) coincida con el elegido. Las asignaciones de "par" siempre requieren
                        selección manual — no existe una regla automática de negocio confirmada para elegirlos.
                      </p>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="codigo_banco">Nivel organizacional:</label>
                          <select id="codigo_banco" name="codigo_banco" value={formGenerar.codigo_banco} onChange={handleGenerarCampoChange} required>
                            <option value="">Seleccione...</option>
                            {niveles.map((n) => (
                              <option key={n.id_banco} value={n.codigo}>{n.nombre} ({n.codigo})</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="codigo_modelo">Modelo de ponderación:</label>
                          <select id="codigo_modelo" name="codigo_modelo" value={formGenerar.codigo_modelo} onChange={handleGenerarCampoChange} required>
                            <option value="">Seleccione...</option>
                            {MODELOS_PONDERACION.map((m) => (
                              <option key={m.codigo} value={m.codigo}>{m.etiqueta}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button type="submit" className="save-button" disabled={generando}>
                        {generando ? 'Generando...' : 'Generar ahora'}
                      </button>
                    </form>
                  )}

                  {resultadoGeneracion && (
                    <div className="resultado-generacion">
                      <h3>Resultado de la generación</h3>
                      <ul>
                        <li>Empleados procesados: <strong>{resultadoGeneracion.empleados_procesados}</strong></li>
                        <li>Evaluaciones FASE2 nuevas: <strong>{resultadoGeneracion.evaluaciones_creadas ?? 0}</strong></li>
                        <li>Evaluaciones FASE2 que ya existían: <strong>{resultadoGeneracion.evaluaciones_ya_existentes ?? 0}</strong></li>
                        <li>Asignaciones nuevas creadas: <strong>{resultadoGeneracion.asignaciones_creadas}</strong></li>
                        <li>Asignaciones que ya existían: <strong>{resultadoGeneracion.asignaciones_ya_existentes}</strong></li>
                      </ul>
                      {resultadoGeneracion.pendientes_manual.length > 0 && (
                        <>
                          <p className="nivel-aviso">
                            {resultadoGeneracion.pendientes_manual.length} fuente(s) quedaron pendientes de asignación manual
                            (jefe/colaborador sin estructura organizacional cargada, o pares — que siempre son manuales):
                          </p>
                          <ul className="pendientes-lista">
                            {resultadoGeneracion.pendientes_manual.slice(0, 20).map((p, i) => (
                              <li key={i}>Empleado #{p.id_empleado} — {p.fuente}: {p.motivo}</li>
                            ))}
                            {resultadoGeneracion.pendientes_manual.length > 20 && (
                              <li>… y {resultadoGeneracion.pendientes_manual.length - 20} más.</li>
                            )}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-buttons">
                  <button type="button" className="cancel-button" onClick={handleCerrarResumen}>Cerrar</button>
                  {periodoSeleccionado.estado === 'BORRADOR' && !showConfirmActivar && (
                    <button type="button" className="save-button" onClick={handlePedirActivacion}>Activar Período</button>
                  )}
                  {periodoSeleccionado.estado === 'BORRADOR' && showConfirmActivar && (
                    <button type="button" className="save-button confirm-activar-button" onClick={handleConfirmarActivacion}>
                      Sí, entiendo, activar ahora
                    </button>
                  )}
                  {periodoSeleccionado.estado === 'ACTIVO' && !showGenerarForm && (
                    <button type="button" className="save-button" onClick={() => setShowGenerarForm(true)}>Generar Evaluaciones</button>
                  )}
                  {periodoSeleccionado.estado === 'ACTIVO' && (
                    <button type="button" className="delete-button" onClick={() => handleCerrarPeriodo(periodoSeleccionado)}>Cerrar Período</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading">Cargando períodos...</div>
          ) : periodos.length === 0 ? (
            <p className="no-data">No hay períodos configurados todavía.</p>
          ) : (
            <div className="crud-table-container">
              <table className="crud-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Vigencia</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {periodos.map((p) => (
                    <tr key={p.id_periodo}>
                      <td>{p.codigo}</td>
                      <td>{p.nombre}</td>
                      <td><span className={`estado-badge ${estadoBadgeClase(p.estado)}`}>{p.estado}</span></td>
                      <td>{p.fecha_inicio || '—'} a {p.fecha_fin || '—'}</td>
                      <td className="acciones">
                        <div className="acciones-inner">
                          <button className="edit-button" onClick={() => handleVerResumen(p)}>
                            <Icon name="eye" size={14} /> Ver resumen
                          </button>
                          {p.estado === 'BORRADOR' && (
                            <button className="edit-button" onClick={() => handleEditClick(p)}>
                              <Icon name="edit" size={14} /> Editar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}

export default PeriodosCRUD;
